import { createMessages } from './hooks/useI18n';

export const { messages, defaultTranslations } = createMessages({
  shared: {
    expandTable: 'Expand table',
    quantity: 'Quantity',
    batchNumber: 'Batch no',
    price: 'Price',
    update: 'Update',
    search: 'Search',
    searchArticleNumber: 'Product',
    searchPlaceholder: 'Search ...',
    searchName: 'Name ...',
    searchArticleNoPlaceholder: 'Product number ...',
    searchErpIdNamePlaceholder: 'Erp id or name ...',
    searchEmailNamePlaceholder: 'Email or name ...',
    excludeCustomerTypePlaceholder: 'Customer type ...',
    excludeCustomerTypeTitle: 'Exclude customer type(s)',
    shrinkTable: 'Shrink table',
    login: 'Login',
    logout: 'Logout',
    development: 'Development',
    settings: 'Settings',
    widgetsAccess: 'Widget access',
    otherInformation: 'Other information',
    stockType: 'Stock type',
    productGroup: 'Product group',
    refillVolume: 'Production quantity',
    minimumStock: 'Minimum stock level',
    noDataAvailable: 'No data available',
    details: 'Details',
    showDetails: 'Show details',
    hideDetails: 'Hide details',
    hide: 'Hide',
    loadMore: 'Load more',
    noItems: 'No items',
    listingItemsCount: 'Listing {count} items',
    all: 'All',
    enabled: 'Enabled',
    disabled: 'Disabled',
    standard: 'Standard',
    sortBy: 'Sort by',
    unknown: 'Unknown',
    customer: 'Customer',
    generalCustomerSettings: 'General customer settings',
    switchLogin: 'Switch login',
    selectLogin: 'Select login',
    endImpersonation: 'End impersonation',
    country: 'Country',
    brand: 'Brand',
    total: 'Total',
    remaining: 'Remaining',
    totalPrice: 'Total price',
    totalQuantity: 'Total quantity',
    assortmentAdminCostDescription: 'Administration handling fee below',
    quantityAdjusted:
      'Quantity has been adjusted since the quantity per delivered unit is {qtyPerUnit}',
    quantityAdjustedInfo:
      'The quantity of some articles have been adjusted to match the quantity per delivered unit',
    default: 'Default',
    branch: 'Branch',
    email: 'Email',
    telephone: 'Telephone',
    fax: 'Fax',
    reference: 'Reference',
    references: 'Reference(s)',
    showAll: 'Show all',
    currency: 'Currency',
    orderCurrency: 'Order currency',
    status: 'Status',
    type: 'Type',
    export: 'Export',
    project: 'Project',
    projects: 'Project(s)',
    projectsPage: 'Projects',
    backToProjects: 'Back to projects',
    linkedAssortments: 'Linked assortments',
    texasTeamGlobal: 'Texas team (all branches)',
    parentAssortment: 'Parent assortment',
    assortment: 'Assortment',
    error: 'Error',
    internalServerError:
      'The issue has been reported and we are working on a solution',
    file: 'File',
    optional: 'Optional',
    download: 'Download',
    save: 'Save',
    show: 'Show',
    chooseFile: 'Choose file',
    createNew: 'Create new',
    created: 'Created',
    language: 'Language',
    name: 'Name',
    done: 'Done',
    add: 'Add',
    added: 'Added',
    remove: 'Remove',
    delete: 'Delete',
    copy: 'Copy',
    clearSelection: 'Clear selection',
    removeConfirmation: 'Remove {name}?',
    deleteConfirmation: 'Delete {name}?',
    selectionCount: '{count} selected',
    confirmation: 'Are you sure?',
    deleteSelected: 'Delete selected',
    selectAll: 'Select all',
    unselectAll: 'Unselect all',
    categories: 'Categories',
    orderTypes: 'Order type(s)',
    deliveryStatuses: 'Delivery status(es)',
    color: 'Color',
    sync: 'Sync',
    syncSchedule: 'Sync schedule',
    connections: 'Connections',
    texasApi: 'Texas API',
    syncTexasArticles: 'Reconcile Texas articles',
    syncTexasArticleChanges: 'Sync Texas article changes',
    texasArticleChangesQueued:
      'Texas article changes sync queued for all branches',
    texasArticlesSyncQueued: 'Texas article metadata sync queued for {branch}',
    checking: 'Checking',
    connected: 'Connected',
    unavailable: 'Unavailable',
  },

  assortment: {
    assortment: 'Assortment',
    assortments: 'Assortments',
    allAssortments: 'All assortments',
    subAssortment: 'Sub-assortment',
    displayFrozenArticles: 'Display frozen articles',
    copyAssortment: 'Copy assortment {assortmentName} from branch {branch}',
    copyAssortmentResult: 'Assortment copy result',
    createAssortment: 'Create assortment',
    createSubAssortment: 'Create sub-assortment',
    addArticles: 'Add products',
    clearArticles: 'Clear products',
    deleteAssortments: 'Delete assortments',
    deleteArticleFromSelected: 'Delete product from assortment',
    useStockLocations: 'Show local multistock only',
    useLocalBranchStock: 'Show local branch stock only',
    useStockBatch: 'Enable batching for supported articles',
    allowAutomaticOrder: 'Allow automatic order',
    allowAutomaticOrderOnIncomingStock:
      'Allow incoming stock dates on automatic orders',
    useAutomaticQuantityAdjustment:
      'Automatically adjust cart quantity to match quantity per unit',
    queueOrderRows: 'Queue order rows missing batch number',
    queueOrderRowsInfo:
      'The queue function keeps order rows under supervision during the waiting stage, and prioritizes older rows first once allocation becomes possible.',
    activateAdminPriceThreshold: 'Add admin cost on checkout',
    assortmentCurrency: 'Currency',
    adminPrice: 'Price',
    currencyDepends: 'Depends on branch',
    adminPriceTriggerThreshold:
      'Remove administrator cost if total order value is above',
    adminPriceCurrency:
      'Price is calculated with the assortments branch currency and will be converted to the customers local currency on checkout',
    draftQuantityAdjustmentPercent:
      'Draft order quantity adjustment allowed in percent',
    assortmentCopied: 'Assortment copied',
    assortmentCopiedSuccess: 'Assortment copied successfully',
    assortmentCreated: 'Assortment created',
    assortmentUpdated: 'Assortment updated',
    assortmentRemoved: 'Assortment deleted',
    assortmentsRemoved: 'Assortments deleted',
    assortmentArticlesAdded: 'Assortment products added',
    assortmentArticlesCleared: 'Assortment products cleared',
    assortmentArticleRemoved: 'Assortment product deleted',
    createChildAssortmentTitle:
      'Create sub-assortment in {parentAssortmentName} ',
    assortmentRemovalWarning:
      'Deleting an assortment will delete customer logins, sub-assortments and products',
    assortmentsRemovalWarning:
      'Deleting assortments will delete its customer logins, sub-assortments and products. This action cannot be undone.',
    assortmentsRemoveConfirmation:
      'You have selected {nrOfRows} row(s) for deletion',
    assortmentsArticleRemoveWarning:
      'The selected product will be removed from the selected assortments. Are you sure?',
    assortmentsArticleRemoveConfirmation:
      'You have selected {nrOfRows} assortment(s)',
    assortmentArticlesMissing: 'Articles missing',
    assortmentArticlesMissingWarning:
      'Some articles could not be copied because they do not exist in the selected branches. Click the download button below to receive a full report.',
    assortmentArticlesMissingExport: 'Export list of missing articles',
    articlesTab: 'Articles',
    teamTab: 'Team',
    role: 'Role',
    global: 'Global',
    mainContact: 'Main contact',
    shortageTab: 'Shortage',
    generalTab: 'General',
    uploadShortageFile: 'Upload shortage file',
    downloadShortageTemplate: 'Download template',
    shortageAssortmentUploaded: 'Shortage file uploaded',
    shortageAssortmentToggled: 'Shortage file status updated',
    shortageAssortmentEnabled: 'Enabled',
    shortageAssortmentDisabled: 'Disabled',
    shortageAssortmentNoFiles: 'No shortage files uploaded yet',
    shortageAssortmentArticleCount: '{count} articles',
    shortageAssortmentUploadedBy: 'Uploaded by',
    shortageAssortmentCreatedDate: 'Uploaded date',
    shortageAssortmentFileName: 'File name',
    shortageAssortmentStatus: 'Status',
    shortageAssortmentActions: 'Actions',
    shortageAssortmentUploadFailed: 'Failed to upload shortage file',
    shortageAssortmentDownload: 'Download file',
    shortageAssortmentEnableAction: 'Enable',
    shortageAssortmentDisableAction: 'Disable',
    shortageSourceGarp: 'Garp',
    shortageSourceFileUpload: 'File upload',
    downloadLiveShortageConfigs: 'Download live assortment shortage configs',
  },

  customer: {
    unknown: 'Unknown',
    brand: 'Brand',
    vendor: 'Vendor',
    agent: 'Agent',
    internal: 'Internal',
    customerType: 'Customer type',
    invoiceCustomer: 'Invoice customer',
    adminCostThresholdLabel: 'Admin cost threshold',
    saveGeneralCustomerSettings: 'Save general customer settings',
    customerGeneralSettingsSaved: 'Customer settings saved!',
    customerApi: 'Customer API',
    issueApiKey: 'Issue new API key',
    apiKeyIssued: 'API key issued',
    apiKeyIssuedDescription:
      'Copy this API key now. It will not be shown again.',
    closeApiKey: 'Close',
    apiKeyId: 'Key ID',
    apiKeyIssuedBy: 'Issued by',
    apiKeyIssuedAt: 'Issued at',
    apiKeyActive: 'Active',
    apiKeyRevoked: 'Revoked',
    revokeApiKey: 'Revoke',
    revokeApiKeyConfirmation: 'Are you sure you want to revoke this API key?',
    reactivateApiKey: 'Reactivate',
    reactivateApiKeyConfirmation:
      'Are you sure you want to reactivate this API key?',
    exposeApiForCustomer: 'Expose customer administration of API',
    allowCartImportForCustomer: 'Allow import of products to cart from file',
    apiAccess: 'API access',
    apiAccessRequired: 'API settings are not enabled for this customer login.',
    apiLogsTitle: 'API key #{apiKeyId} logs',
    apiLogDetails: 'Request details',
    apiLogTime: 'Time',
    apiLogMethod: 'Method',
    apiLogRequest: 'Request',
    apiLogStatus: 'Status',
    apiLogDuration: 'Duration',
    apiLogTraceId: 'Trace ID',
    apiLogRouteValues: 'Route values',
    apiLogRequestBody: 'Request body',
    apiLogResponseBody: 'Response body',
    apiLogTruncated: 'truncated',
    openApiSwagger: 'Open Swagger',
  },

  supplier: {
    supplier: 'Supplier',
    suppliers: 'Suppliers',
    allSuppliers: 'All suppliers',
    supplierNumber: 'Supplier no',
    selectSupplierHelp: 'Select a supplier to inspect purchase history',
    backToSuppliers: 'Back to suppliers',
    searchPlaceholder: 'Supplier number or name ...',
    allCountries: 'All countries',
    purchaseActivity: 'Purchase activity',
    allPurchaseActivity: 'All purchase activity',
    hasOpenPurchaseOrders: 'Has open purchase orders',
    noOpenPurchaseOrders: 'No open purchase orders',
    openPurchaseOrders: 'Open purchase orders',
    openPurchaseValue: 'Open purchase value',
    nextDelivery: 'Next delivery',
    purchaseOrders: 'Purchase orders',
    purchaseOrder: 'Purchase order',
    purchaseOrderCount: '{count} purchase orders',
    supplierInformation: 'Supplier information',
    commercialTerms: 'Commercial terms',
    address: 'Address',
    paymentTerms: 'Payment terms',
    deliveryTerms: 'Delivery terms',
    deliveryMethod: 'Delivery method',
    allStatuses: 'All statuses',
    active: 'Active',
    frozen: 'Frozen',
    lastPurchaseSync: 'Last purchase sync',
    purchaseOrdersSynchronized: 'Purchase orders synchronized',
    ordered: 'Ordered',
    expected: 'Expected',
    articles: 'Articles',
    openQuantity: 'Open quantity',
    received: 'Received',
    partiallyReceived: 'Partially received',
    overdue: 'Overdue',
    open: 'Open',
  },

  purchase: {
    purchases: 'Purchases',
    purchaseOrder: 'Purchase order',
    purchaseOrders: 'Purchase orders',
    backToPurchases: 'Back to purchases',
    selectPurchaseHelp: 'Select a purchase order to inspect its line items',
    searchPlaceholder: 'Order number or reference ...',
    supplierSearchPlaceholder: 'Supplier number or name ...',
    articleSearchPlaceholder: 'Article number or description ...',
    allStatuses: 'All statuses',
    open: 'Open',
    overdue: 'Overdue',
    partiallyReceived: 'Partially received',
    received: 'Received',
    ordered: 'Ordered',
    expected: 'Expected',
    goodsReadyAtSupplier: 'Goods Ready at Supplier',
    transportTime: 'Transport time',
    transportTimeDays: '{days} days',
    goodsArrivingToRh: 'Goods arriving to RH',
    articles: 'Articles',
    openQuantity: 'Open quantity',
    purchaser: 'Purchaser',
    orderInformation: 'Order information',
    deliveryInformation: 'Delivery information',
    invoiceSupplier: 'Invoice supplier',
    orderType: 'Order type',
    paymentTerm: 'Payment term',
    deliveryTerm: 'Delivery term',
    deliveryMethod: 'Delivery method',
    season: 'Season',
    yourReference: 'Your reference',
    ourReference: 'Our reference',
    goodsMarking: 'Goods marking',
    deliveryAddress: 'Delivery address',
    orderValue: 'Order value',
    deliveredValue: 'Delivered value',
    remainingValue: 'Remaining value',
    progress: 'Progress',
    lineItems: 'Purchase order lines',
    row: 'Row',
    unitPrice: 'Unit price',
    discount: 'Discount',
    latestSyncDate: 'Latest sync date',
  },

  documents: {
    documents: 'Documents',
    name: 'Name',
    description: 'Description',
    generalDocuments: 'General documents',
    branchDocuments: 'Documents for {branch}',
    yourDocuments: 'Your documents',
    newDocument: 'New document',
    confirmRemoveDocument: 'Are you sure you want to remove this document?',
    removeDocument: 'Remove document',
    documentRemoved: 'Document removed',
  },

  notifications: {
    notifications: 'Notifications',
    name: 'Title',
    message: 'Message',
    newNotification: 'New notification',
    updateNotification: 'Update notification',
    confirmRemoveNotification:
      'Are you sure you want to remove this notification?',
    removeNotification: 'Remove notification',
    notificationRemoved: 'Notification removed',
  },

  reports: {
    reports: 'Reports',
    articleStatistics: {
      report: 'Product statistics',
      validationMessage: 'Specify branch, category and time period',
      validationMessageBrand: 'Specify category and time period',
    },
    articleCategory: 'Product category',
    branchPlaceholder: 'Select branches',
    categories: {
      clear: 'Clear',
      selectAll: 'Select all',
      filterPlaceholder: 'Filter available categories',
      noMatchingCategories: 'No matching categories',
      selectABranch: 'Select a branch to show categories',
      noCategoriesSelected: 'No categories selected',
    },
    customerOrders: {
      report: 'Customer orders',
      validationMessage:
        'Specify branch, category, customer type and time period',
      validationMessageBrand: 'Specify category and time period',
    },
    customerTypePlaceholder: 'Select customer types',
    downloadReport: 'Download report',
    exportArticles: 'Export articles',
    exportArticleVariants: 'Export articles with variants',
    currentLocation: '(Current location)',
    allLocations: '(All locations)',
    includeFrozenArticles: 'Include frozen products',
    includeImages: 'Include images',
    includeShortageList: 'Include shortage list',
    itemType: 'Item type',
    itemTypeFilter: 'Filter on item type',
    itemTypePlaceholder: 'Select item types',
    timePeriod: 'Time period',
    timePeriodPlaceholder: 'Select a time period',
    currencies: 'Currencies',
    currencyPlaceholder: 'Select currencies',
    selectBranchAndBrandToSeeProjects:
      'Select one branch and a brand to see available currencies',
    projectOptional: 'Project - optional',
    projectPlaceholder: 'Search erp id or name',
    selectBranchToSeeProjects: 'Select one branch to see available projects',
    tooManyBranchesSelected: 'Too many branches selected',
    brandPlaceholder: 'Select brand',
    selectProjectToSeeBrands: 'Select a project to see available brands',
  },

  article: {
    article: 'Product',
    articles: 'Products',
    articlesSummary: 'Grouped orders summary',
    articleNumber: 'Product no',
    articleNumberOrDescription: 'Product no or description',
    description: 'Description',
    extraDescription: 'Extra description',
    kit: 'Kit',
    matrix: 'Matrix',
    frozen: 'Frozen',
    frozenAlert: "This article is frozen and can't be ordered",
    size: 'Size',
    unit: 'Unit',
    quantityPerUnit: 'Qty/unit',
    color: 'Color',
    showSummary: 'Show summary',
    showTopContent: 'Show top content',
    hideSummary: 'Hide summary',
    hideTopContent: 'Hide top content',
    articleInformationTitle: 'Product information',
    openArticleDetails: 'Open article details',
    openInTexas: 'Open in Texas',
    notFoundInTexas: 'Does not exist in Texas',
    notCheckedInTexas: 'Not checked in Texas',
    ambiguousInTexas: 'Multiple matches in Texas',
    articleCategory: 'Product category',
    articleType: 'Product type',
    allArticleTypes: 'All product types',
    includeFrozen: 'Include frozen products',
    selectArticleHelp:
      'Select a product to inspect branch-specific product data',
    backToArticles: 'Back to products',
    batchStock: 'Batch stock',
    active: 'Active',
    stockPlanning: 'Stock planning',
    source: 'Source',
    currentLevel: 'Current level',
    ofMinimum: '{percent}% of minimum',
    lastSynchronized: 'Last synchronized {date}',
    lastSync: 'Last sync: {date}',
    texasProjectNumber: 'Project number (Texas)',
    texasMetadataSync: 'Texas metadata last synced',
    lastMovement: 'Last movement',
    overview: 'Overview',
    noRefillRequired: 'No refill action required for this branch.',
    refillRequired: 'Stock is below the configured minimum.',
    additionalInformation: 'Additional information',
    sellingPrice: 'Selling price',
    priceDecimals: 'Price decimals',
    priceLists: 'Price lists',
    dataQuality: 'Data quality',
    dataSynchronized:
      'All required product attributes are synchronized for {branch}.',
    localBranchStock: 'Local branch stock',
    refillVolume: 'Production quantity',
    minimumStock: 'Minimum stock level',
    noRefillRequiredShort: 'No refill required',
    refillRequiredShort: 'Refill required',
    synchronized: 'Synchronized',
    linkedAssortmentsCount: '{count} linked',
    acrossConfiguredBranches: 'Across configured branches',
    configuredBranches: 'Configured branches',
    noLinkedAssortments: 'This product is not linked to any assortments.',
    assortmentId: 'Assortment #{id}',
    openAssortment: 'Open assortment {name}',
    stockLocations: 'Stock locations',
    locationsCount: '{count} locations',
    volumesInUnit: 'Volumes in {unit}',
    actual: 'Actual',
    outbound: 'Outbound',
    outboundReserved: 'Outbound Reserved',
    inbound: 'Inbound',
    date: 'Create date',
    noStockLocations: 'No stock locations configured for this branch.',
    branchStock: 'Branch stock',
    stockTypeValue: 'Stock type {value}',
    stockCalculation:
      'Available = actual - outbound - outbound reserved. Balance = available + inbound.',
  },

  stock: {
    stock: 'Stock',
    location: 'Location',
    inStock: 'In stock',
    inOrder: 'In order',
    PO: 'PO',
    balance: 'Balance',
    available: 'Available',
  },

  cart: {
    cart: 'Cart',
    quantityInCart: 'Quantity in cart',
    checkout: 'Checkout',
    updateCart: 'Update cart',
    unavailableArticle: 'This product is no longer available',
    removeUnavailableArticles: 'Please remove unavailable products from cart',
    importFile: 'Import from file',
    importFileFormat:
      'Excel (.xlsx) or CSV: article number, quantity and an optional PO number in the third column',
    importSuccess: '{count} products added to cart',
  },

  order: {
    order: 'Order',
    orders: 'Orders',
    groupedOrders: 'Grouped orders: ',
    seeSummary: 'See summary',
    closeSummary: 'Close summary',
    placeOrder: 'Place order',
    referenceOrderNumber: 'Reference order no',
    message: 'Message',
    careLabelPos: 'CareLabel PO´s',
    orderNumber: 'Order number',
    reference: 'Reference',
    externalReference: 'External reference',
    internalReference: 'Internal reference',
    orderDate: 'Order date',
    confirmedDeliveryDate: 'Confirmed delivery date',
    confirmedDate: 'Confirmed',
    latestDate: 'Latest',
    deliveryDate: 'Delivery date',
    deliveryDates: 'Delivery dates',
    deliveryStatus: 'Delivery status',
    progress: 'Progress',
    progresses: 'Progresses',
    unconfirmed: 'Unconfirmed',
    confirmed: 'Confirmed',
    undelivered: 'Undelivered',
    partiallyDelivered: 'Partially delivered',
    delivered: 'Delivered',
    unplaced: 'Unplaced',
    placed: 'Placed',
    awaitingDelivery: 'Awaiting delivery',
    billedTo: 'Billed to',
    paymentTerms: 'Payment terms',
    deliveryTerms: 'Delivery terms',
    deliveryMethod: 'Delivery method',
    noMessage: 'No message',
    row: 'Row',
    deliveryNote: 'Delivery note',
    invoiceNumber: 'Invoice number',
    placeAsVendor: 'Place order as vendor',
    orderPlacedTitle: 'Order was placed',
    orderTypeForecast: 'Forecast',
    orderTypeStandard: 'Standard',
    orderTypeSample: 'Call Off / SMS',
    orderTypeVariable: 'Carelabel',
    orderPlacedDescription:
      'Your order has now been submitted and will be processed shortly. You can follow the progress of your order by checking your order history',
    splitOrderDescription: 'Your order has been split into multiple orders.',
    trackingNumber: 'AWB number',
    updateOrder: 'Update order',
    orderType: 'Order type',
    placeVariableOrderTitle: 'Carelabel order',
    placeVariableOrderDescription:
      'To check the current status, please follow the PO-links',
    contactName: 'Contact name',
    contactPhone: 'Contact phone',
    courierAccount: 'Courier account',
    selectAddress: 'Select address',
    origin: 'Origin',
    deliveryPickerDisabledTooltipOutOfStock:
      'Only optional if articles are in stock',
    deliveryPickerDisabledTooltipMultiStock:
      'Delivery date selection is not available for one or more products in cart',
    additionalFields: {
      title: 'Additional',
      brandName: 'Brand name',
      garmentType: 'Garment type',
      garmentQty: 'Garment quantity',
      segment: 'Segment',
    },
    season: 'Season',
    packStatus: 'Pack status',
    reservedForPacking: 'Reserved (5)',
    underPacking: 'Under packing (6)',
    packed: 'Ready (7)',
  },

  orderRowQueues: {
    orderRowQueues: 'Order row queues',
    queue: 'Queue',
    pending: 'Pending',
    partlyFilled: 'Partly filled',
    filled: 'Filled',
    cancelled: 'Cancelled',
    originalQuantity: 'Original qty',
    allocatedQuantity: 'Allocated qty',
    added: 'Added',
    allocated: 'Allocated',
    date: 'Date',
    delivered: 'Delivered',
    removed: 'Removed',
    relatedQueue: '{count} related queue',
    relatedQueues: '{count} related queues',
    mixedBatches: 'Mixed',
    queuesInGroup: 'Queues in this group',
    oldestQueueProcessedFirst: 'Oldest queue is processed first',
    queueNumber: 'Queue #{id}',
    original: 'Original',
    continuation: 'Continuation',
    createdFromOrderRow: 'Created from order {orderNumber}, row {rowNumber}',
    remainingQuantityAfterAllocation: 'Remaining quantity after allocation',
    allocatedValue: '{quantity} allocated',
    requestedValue: 'of {quantity} requested',
    waitingValue: '{quantity} waiting',
    noBatchYet: 'No batch yet',
    olderThanDays: 'Older than X days',
    days: 'Days',
    daysValue: '{count} days',
  },

  address: {
    address: 'Address',
    addresses: 'Addresses',
    customAddress: 'New custom address',
    shipping: 'Shipping',
    billing: 'Billing',
    company: 'Company',
    address1: 'Address 1',
    address2: 'Address 2',
    city: 'City',
    postalCode: 'Postal code',
    country: 'Country',
    shippingTypeStandard: 'Standard',
    shippingTypeNewCustom: 'New',
    shippingTypeExistingCustom: 'Saved',
  },

  invoice: {
    invoices: 'Invoices',
    invoiceNumber: 'Invoice number',
    invoiceDate: 'Invoice date',
    dueDate: 'Due date',
    paid: 'Paid',
    unpaid: 'Unpaid',
    overdue: 'Overdue',
  },

  login: {
    logout: 'Logout',
    noAvailableLogins: 'No available customer logins',
  },

  session: {
    lockedTitle: 'Your customer account is locked due to overdue invoices',
    lockedDescription:
      'See your statement in the webshop under Invoices. If you have any questions, please contact finance@rudholm-hk.se',
    maintenanceTitle: 'Country is undergoing planned maintenance',
    maintenanceDescription:
      'We are currently undergoing a planned maintenance in this country, no orders can therefor be placed. Please try again later or contact customer service for more information.',
  },

  users: {
    user: 'User',
    users: 'Users',
    name: 'Name',
    email: 'Email',
    userUpdated: 'User updated',
    editUser: 'Edit user',
    customerLogins: 'Customer logins',
    customerLoginRemoveConfirmation: 'Delete this login?',
    customerLoginRemoved: 'Customer login deleted',
    customerLoginUpdated: 'Customer login updated',
    customerLoginsRemoved: 'Customer logins deleted',
    customerLoginsRemovalWarning:
      'Are you sure you want to delete all selected customer logins? This action cannot be undone.',
    customerLoginsRemoveConfirmation:
      'You have selected {nrOfRows} row(s) for deletion',
  },

  branches: {
    branches: 'Branches',
    copyToBranches: 'Copy to branches',
    customsExport: 'Customs export',
    rowNumber: 'Row no',
    articleDescription: 'Description',
    productGroupUpdated: 'Product group updated',
    editProductGroup: 'Edit product group: {erpId}',
    customsExportProductGroup: 'Customs export product group',
    customsExportExpandMatrix: 'Customs export expand matrix',
    expandMatrix: 'Expand matrix',
    maintenanceDescription:
      'During maintenance mode, new orders can not be placed in this branch until maintenance mode is disabled.',
  },

  dashboard: {
    dashboard: 'Dashboard',
    welcome: 'Welcome',
    contactRH: 'Contact Rudholm',
    news: 'Latest news',
    invoices: {
      title: 'Overdue invoices',
      noneDue: 'No invoces are overdue',
      viewAll: 'View all invoices',
      viewAllUnpaid: 'View all unpaid invoices',
    },
  },

  validation: {
    mixed: {
      default: 'Invalid',
      required: 'Required field',
      oneOf: 'Must be one of the following values: {values}',
      notOneOf: 'Must not be one of the following values: {values}',
      notType: 'Must be a {type} type',
    },
    string: {
      length: 'Must be exactly {length} characters',
      min: 'Must be at least {min} characters',
      max: 'Must be at most {max} characters',
      matches: 'Must match the following: "{regex}"',
      email: 'Must be a valid email',
      url: 'Must be a valid URL',
      trim: 'Must be a trimmed string',
      lowercase: 'Must be a lowercase string',
      uppercase: 'Must be a upper case string',
      invalidChars: 'Contains invalid characters',
    },
    number: {
      min: 'Must be greater than or equal to {min}',
      max: 'Must be less than or equal to {max}',
      lessThan: 'Must be less than {less}',
      moreThan: 'Must be greater than {more}',
      notEqual: 'Must be not equal to {notEqual}',
      positive: 'Must be a positive number',
      negative: 'Must be a negative number',
      integer: 'Must be an integer',
    },
    date: {
      min: 'Must be later than {min}',
      max: 'Must be at earlier than {max}',
    },
    object: {
      noUnknown: 'Cannot have keys not specified in the object shape',
    },
    array: {
      min: 'Must have at least {min} items',
      max: 'Must have less than or equal to {max} items',
    },
    file: {
      maxSizeExceeded: 'File exceeds max size of {maxSize}',
      unsupportedFileType: 'File type is unsupported',
      multipleNotAllowed: 'Multiple files is not allowed',
      multipleMaxSizeExceeded: 'Total max size for files is exceeded',
      multipleMaxCountExceeded: 'Too many files selected',
    },
  },
  checkoutAdapter: {
    checkoutAdapters: 'Checkout Adapters',
    _3M: {
      addToCart: 'Add to cart',
      removeFromCart: 'Remove from cart',
      confirmRead: 'I have read, and agree to follow the ',
      confirmReadLink: '3M Scotchlite Product and Brand Usage Standards',
      cartIsFull: 'Your cart is currently full',
      clearCart: 'Clear cart',
      lineItemMessage: 'Cart',
      order: {
        poNumber: '3M PO-number or distributor name',
        brand: 'Brand',
        factory: 'Factory',
        reflectiveProduct: 'Reflective product',
        segment: 'Segment',
        contactName: 'Contact name',
        contactPhone: 'Contact phone',
        courierAccount: 'Courier account',
        message: 'Message',
        producedGarment: 'Produced garment',
        garmentType: 'Garment type',
        garmentQuantity: 'Garment quantity',
        quantity: 'Quantity',
      },
      unavailableProduct: 'Product is no longer available',
    },
  },
  orderMetaFields: {
    confirmRemoveMetaFieldOption:
      'Are you sure you want to remove option {name}?',
    createOrderMetaFieldOption: 'Create option',
    editOrderMetaFields: 'Edit order meta fields',
    isAlreadyParentOption: 'Is already a parent option',
    isSubOption: 'Is suboption',
    orderMetaField: 'Order meta field',
    orderMetaFieldOptionCreated: 'Option {name} created',
    orderMetaFieldOptionRemoved: 'Option {name} removed',
    orderMetaFieldOptionUpdated: 'Option {name} updated',
    parentMetaField: 'Parent meta field',
    parentOption: 'Parent option',
    removeMetaFieldOption: 'Remove option',
    updateOrderMetaFieldOption: 'Update option',
    value: 'Value',
  },
  customers: {
    customers: 'Customers',
  },
  widgets: {
    widgets: 'Widgets',
    widgetType: 'Widget type',
    widgetCreated: 'Widget created',
    widgetUpdated: 'Widget updated',
    widgetRemoved: 'Widget removed',
    widgetNamePlaceholder: 'Name your widget',
    customerMustBeBrand: 'Widgets disabled, customer must be of type "Brand"',
    widgetRemovalWarning:
      'Are you sure you want to remove this widget from your dashboard?',
    exportShortageList: 'Export shortage list',
    articlesInShortage: 'Articles in shortage',
    create: 'Create new widget',
    insufficientCompetence:
      'Insufficient competence for this widget, please remove this widget and recreate a new one.',
    errorRetrievedWidget: 'Could not retrieve widget',
    selectCurrencies: 'Select currencies - optional',
    categories: {
      selectCategory: 'Select a category',
      createCategory: 'Create new category',
      updateCategory: 'Update category',
      deleteCategory: 'Delete category',
      categoryCreated: 'Category created',
      categoryUpdated: 'Category updated',
      categoryRemoved: 'Category removed',
      removalWarning:
        'Are you sure you want to remove this category? This will move all its widgets to uncategorized',
      customerRemovalWarning:
        'Are you sure you want to remove this category? This will delete all the widgets within the category',
      noCategories: 'You have not created any categories',
    },
    lastGeneratedReportFailed:
      'Last generated report failed, please try generating a new one. You can still download the last successfully generated report below.',
    pendingReport: 'There is currently one pending report',
    noReportsGeneratedYet: 'There are no generated reports yet for this widget',
    downloadExport: 'Download latest report',
    generated: 'Generated',
    awaitingDeliveries: {
      create: 'Create awaiting deliveries widget',
      update: 'Update awaiting deliveries widget',
      widgetText: 'Awaiting delivery orders',
      triggerExport: 'Export awaiting deliveries',
    },
    shortage: {
      create: 'Create shortage widget',
      update: 'Update shortage widget',
      triggerExport: 'Export shortage list',
      articlesInShortage: 'Articles in shortage',
    },
    delayedDeliveries: {
      create: 'Create delayed deliveries widget',
      update: 'Update delayed deliveries widget',
      widgetText: 'Delayed delivery orders',
      triggerExport: 'Export delayed deliveries',
    },
    productStatistics: {
      create: 'Create product statistics report widget',
      update: 'Update product statistics report widget',
      triggerExport: 'Trigger new product statistics report',
      newReportTriggered:
        'A new product statistics report is now under construction',
    },
    customerOrdersReport: {
      triggerExport: 'Trigger new customer orders report',
      create: 'Create customer orders report widget',
      update: 'Update customer orders report widget',
      newReportTriggered:
        'A new customer orders report is now under construction',
    },
    slowStock: {
      triggerExport: 'Trigger new slow stock report',
      create: 'Create slow stock widget',
      update: 'Update slow stock widget',
      newReportTriggered: 'A new slow stock report is now under construction',
    },
    countOrderRowQueues: {
      create: 'Create order row queues widget',
      update: 'Update order row queues widget',
      widgetText: 'Order row queues',
      triggerExport: 'Export order row queues',
    },
  },
  alarms: {
    addAlarm: 'Send email notifications',
  },
});
